import type {
  CourseManifest,
  CourseSlide,
  SavedCourseProgress,
} from "./types";

export const MANIFEST_URL = "/course/media-manifest.json";
export const STORAGE_KEY = "thesis.continuous-training.progress.v1";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export function validateCourseManifest(value: unknown): CourseManifest {
  if (!isRecord(value)) {
    throw new Error("The course manifest is not a JSON object.");
  }

  const requiredStrings = [
    "courseTitle",
    "subtitle",
    "candidate",
    "credential",
    "conclusion",
  ] as const;

  for (const key of requiredStrings) {
    if (!isNonEmptyString(value[key])) {
      throw new Error(`The course manifest is missing ${key}.`);
    }
  }

  if (
    !isFiniteNumber(value.totalSlides) ||
    !isFiniteNumber(value.totalDurationMs) ||
    value.totalDurationMs <= 0
  ) {
    throw new Error("The course manifest has invalid duration metadata.");
  }

  if (
    !Array.isArray(value.slides) ||
    value.slides.length !== value.totalSlides ||
    value.slides.length === 0
  ) {
    throw new Error("The course slide count does not match the manifest.");
  }

  if (!Array.isArray(value.modules) || value.modules.length === 0) {
    throw new Error("The course manifest does not define any modules.");
  }

  if (
    !Array.isArray(value.learningObjectives) ||
    !value.learningObjectives.every(isNonEmptyString)
  ) {
    throw new Error("The course learning objectives are invalid.");
  }

  if (
    !isRecord(value.continuous) ||
    !isNonEmptyString(value.continuous.hls) ||
    !isNonEmptyString(value.continuous.caption) ||
    !isNonEmptyString(value.continuous.poster)
  ) {
    throw new Error("The continuous media paths are missing.");
  }

  const slides = value.slides as unknown[];
  slides.forEach((slideValue, index) => {
    if (!isRecord(slideValue)) {
      throw new Error(`Slide ${index + 1} is invalid.`);
    }

    const stringFields = [
      "title",
      "module",
      "image",
      "transcript",
      "altText",
    ] as const;

    if (!stringFields.every((field) => isNonEmptyString(slideValue[field]))) {
      throw new Error(`Slide ${index + 1} is missing required content.`);
    }

    if (
      !isFiniteNumber(slideValue.id) ||
      !isFiniteNumber(slideValue.moduleId) ||
      !isFiniteNumber(slideValue.durationMs) ||
      !isFiniteNumber(slideValue.timelineStartMs) ||
      !isFiniteNumber(slideValue.timelineEndMs) ||
      slideValue.timelineEndMs <= slideValue.timelineStartMs
    ) {
      throw new Error(`Slide ${index + 1} has invalid timeline metadata.`);
    }

    if (index > 0) {
      const previous = slides[index - 1];
      if (
        isRecord(previous) &&
        isFiniteNumber(previous.timelineEndMs) &&
        Math.abs(previous.timelineEndMs - slideValue.timelineStartMs) > 50
      ) {
        throw new Error(
          `The timeline is not continuous between slides ${index} and ${index + 1}.`,
        );
      }
    }
  });

  return value as unknown as CourseManifest;
}

export function findSlideIndex(
  slides: CourseSlide[],
  timeSeconds: number,
): number {
  if (slides.length === 0) {
    return 0;
  }

  const timeMs = Math.max(0, timeSeconds * 1000);
  let low = 0;
  let high = slides.length - 1;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const slide = slides[middle];

    if (timeMs < slide.timelineStartMs) {
      high = middle - 1;
    } else if (
      timeMs >= slide.timelineEndMs &&
      middle < slides.length - 1
    ) {
      low = middle + 1;
    } else {
      return middle;
    }
  }

  return Math.min(Math.max(low, 0), slides.length - 1);
}

export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }

  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds,
    ).padStart(2, "0")}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function formatDurationLabel(milliseconds: number): string {
  const totalMinutes = Math.round(milliseconds / 60000);
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours} hr` : `${hours} hr ${minutes} min`;
}

export function loadSavedProgress(): SavedCourseProgress | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<SavedCourseProgress>;
    if (
      parsed.version !== 1 ||
      !isFiniteNumber(parsed.currentTime) ||
      !isFiniteNumber(parsed.playbackRate) ||
      !isFiniteNumber(parsed.volume) ||
      typeof parsed.muted !== "boolean" ||
      typeof parsed.captionsEnabled !== "boolean" ||
      typeof parsed.transcriptOpen !== "boolean" ||
      typeof parsed.completed !== "boolean" ||
      !isNonEmptyString(parsed.updatedAt)
    ) {
      return null;
    }

    return parsed as SavedCourseProgress;
  } catch {
    return null;
  }
}

export function saveProgress(progress: SavedCourseProgress): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // The course remains fully usable if storage is unavailable.
  }
}
